<template>
    <!-- Sem card próprio: o container externo (CollapsibleInfoCard) já emoldura -->
    <div class="text-stone-900 dark:text-stone-100">
        <!-- Header -->
        <div class="flex flex-wrap items-center justify-between gap-2 px-2 py-2 border-b border-stone-200 dark:border-white/10">
            <div class="flex items-center gap-2">
                <h4 class="xp-display text-[11px] font-bold uppercase tracking-[0.18em] text-stone-400 dark:text-stone-500">
                    {{ scoreId ? 'Cálculo histórico' : 'Último cálculo' }}
                </h4>
                <span v-if="trace"
                      class="text-[9px] font-semibold uppercase tracking-wider px-1.5 py-px rounded border border-stone-200 dark:border-white/10 text-stone-400 dark:text-stone-500">
                    {{ trace.source === 'batch' ? 'batch noturno' : trace.source }}
                </span>
                <span v-if="trace && trace.route === 'revisao_humana'"
                      class="text-[9px] font-semibold uppercase tracking-wider px-1.5 py-px rounded border border-purple-500/30 bg-purple-500/10 text-purple-600 dark:text-purple-400">
                    revisão humana
                </span>
            </div>
            <div class="flex items-center gap-3">
                <span v-if="trace" class="xp-mono text-[10px] text-stone-400 dark:text-stone-500">
                    {{ trace.created_at }}
                </span>
                <button
                    v-if="trace"
                    type="button"
                    class="border border-stone-200 dark:border-white/10 text-stone-600 dark:text-stone-300 hover:bg-stone-100 dark:hover:bg-white/5 text-xs font-semibold rounded-lg px-3 py-1.5 transition-colors"
                    @click="openModalAt(null)"
                >
                    Ver extrato completo
                </button>
            </div>
        </div>

        <ticto-loading v-if="loading" />

        <template v-else>
            <!-- Gauge + matemática do run -->
            <div class="px-2 py-3 border-b border-stone-100 dark:border-white/5">
                <div class="flex flex-wrap items-end gap-x-8 gap-y-3">
                    <div>
                        <div class="xp-mono text-5xl font-bold tabular-nums leading-none" :class="finalColor">
                            {{ finalScore }}
                        </div>
                        <div class="xp-mono text-[11px] mt-1.5" :class="finalColor">
                            {{ finalScore < 50 ? 'bloqueado (< 50)' : (finalScore < 75 ? 'merece atenção' : 'confiável') }}
                        </div>
                    </div>

                    <div v-if="rawScore !== null" class="flex items-end gap-x-4 pb-0.5">
                        <div>
                            <div class="xp-display text-[10px] font-bold uppercase tracking-[0.18em] text-stone-400 dark:text-stone-500 mb-1">Soma bruta</div>
                            <div class="xp-mono text-xl font-bold tabular-nums leading-none">{{ rawScore }}</div>
                        </div>
                        <div v-if="ceiling !== null" class="pb-px text-stone-300 dark:text-stone-600">→</div>
                        <div v-if="ceiling !== null">
                            <div class="xp-display text-[10px] font-bold uppercase tracking-[0.18em] text-amber-600 dark:text-amber-400 mb-1">Teto</div>
                            <div class="xp-mono text-xl font-bold tabular-nums leading-none text-amber-600 dark:text-amber-400">{{ ceiling }}</div>
                        </div>
                    </div>

                </div>

                <!-- Régua do score -->
                <div class="mt-4">
                    <div class="relative h-2 rounded-full overflow-hidden bg-stone-100 dark:bg-white/6">
                        <div class="absolute inset-y-0 left-0 bg-red-500/25" :style="{ width: gaugePct(50) + '%' }"></div>
                        <div class="absolute inset-y-0 rounded-full"
                             :class="finalScore < 50 ? 'bg-red-500' : (finalScore < 75 ? 'bg-amber-500' : 'bg-emerald-500')"
                             :style="{ left: 0, width: gaugePct(finalScore) + '%' }"></div>
                        <div class="absolute inset-y-0 border-l border-dashed border-red-400/70" :style="{ left: gaugePct(50) + '%' }"></div>
                        <div v-if="ceiling !== null" class="absolute inset-y-0 border-l-2 border-amber-400" :style="{ left: gaugePct(ceiling) + '%' }"></div>
                    </div>
                    <div class="mt-1.5 flex justify-between xp-mono text-[10px] text-stone-400 dark:text-stone-500">
                        <span>15</span>
                        <span class="text-red-400/80">50 · bloqueio</span>
                        <span>100</span>
                    </div>
                </div>

                <div v-if="absorbedPenalty > 0" class="mt-3 text-[11px] text-amber-700 dark:text-amber-400 bg-amber-500/10 border border-amber-500/30 rounded-lg px-3 py-2">
                    A soma passou de 100: sem teto, até {{ absorbedPenalty }} pontos de penalidade seriam absorvidos pelo cap.
                </div>
            </div>

            <!-- Regras do run (visual do trace) -->
            <div class="divide-y divide-stone-100 dark:divide-white/5">
                <button
                    v-for="row in orderedRows"
                    :key="row.rule"
                    type="button"
                    class="w-full flex items-center gap-3 px-2 py-2 text-left transition-colors"
                    :class="[
                        trace ? 'hover:bg-stone-50 dark:hover:bg-white/3 cursor-pointer' : 'cursor-default',
                        row.delta === 0 && !row.guard ? 'opacity-50' : ''
                    ]"
                    @click="trace && openModalAt(row.rule)"
                >
                    <span class="w-2 h-2 rounded-full shrink-0"
                          :class="row.delta < 0 ? 'bg-red-500' : (row.delta > 0 ? 'bg-emerald-500' : (row.guard ? 'bg-amber-500' : 'bg-stone-300 dark:bg-stone-600'))"></span>
                    <span class="xp-mono text-xs font-medium min-w-0 truncate">{{ row.label }}</span>

                    <span v-if="row.guard"
                          class="text-[9px] font-semibold uppercase tracking-wider px-1.5 py-px rounded border border-amber-500/30 bg-amber-500/10 text-amber-600 dark:text-amber-400">
                        {{ row.guard }}
                    </span>
                    <span v-for="flag in row.flags" :key="flag"
                          class="text-[9px] font-semibold uppercase tracking-wider px-1.5 py-px rounded border border-orange-500/30 bg-orange-500/10 text-orange-600 dark:text-orange-400">
                        {{ flag }}
                    </span>
                    <span v-if="row.ceiling !== null"
                          class="text-[9px] font-semibold uppercase tracking-wider px-1.5 py-px rounded border border-amber-500/30 bg-amber-500/10 text-amber-600 dark:text-amber-400">
                        teto {{ row.ceiling }}
                    </span>

                    <span class="ml-auto flex items-center gap-2 shrink-0">
                        <span v-if="row.valueLabel !== null" class="xp-mono text-[10px] text-stone-400 dark:text-stone-500 tabular-nums">
                            {{ row.valueLabel }}
                        </span>
                        <span class="relative h-1 w-16 rounded-full bg-stone-200 dark:bg-white/10 overflow-hidden">
                            <span class="absolute inset-y-0 left-0 rounded-full"
                                  :class="row.delta < 0 ? 'bg-red-500' : (row.delta > 0 ? 'bg-emerald-500' : 'bg-stone-300 dark:bg-stone-600')"
                                  :style="{ width: Math.min(100, Math.abs(row.delta) / 50 * 100) + '%' }"></span>
                        </span>
                        <span class="xp-mono text-xs tabular-nums w-9 text-right font-semibold"
                              :class="row.delta < 0 ? 'text-red-600 dark:text-red-400' : (row.delta > 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-stone-400 dark:text-stone-500')">
                            {{ row.delta > 0 ? '+' + row.delta : row.delta }}
                        </span>
                    </span>
                </button>
            </div>

            <div v-if="!trace" class="px-2 py-2.5 border-t border-stone-100 dark:border-white/5 text-[11px] text-stone-400 dark:text-stone-500">
                Run sem trace persistido (anterior à esteira): pesos derivados do histórico do score. Rode um novo cálculo pra ver o extrato completo.
            </div>

            <score-trace-modal
                v-if="trace"
                :show="modalOpen"
                :trace="trace"
                :user="user"
                :initial-rule="modalRule"
                @close="modalOpen = false"
            />
        </template>
    </div>
</template>

<script>
import TictoLoading from "../../layouts/TictoLoading.vue";
import ScoreTraceModal from "./ScoreTraceModal.vue";
import { scoreRuleLabels } from "./scoreRuleLabels";

export default {
    name: "ScoreTraceViewer",
    components: { TictoLoading, ScoreTraceModal },
    props: {
        user: { type: Object, required: true },
        scoreId: { type: [String, Number], default: null },
        score: { type: [Object, Boolean], default: null },
    },
    data() {
        return {
            loading: true,
            trace: null,
            modalOpen: false,
            modalRule: null,
        };
    },
    computed: {
        finalScore() {
            if (this.trace) return this.trace.score_final;
            return this.score?.amount ?? 0;
        },
        finalColor() {
            if (this.finalScore < 50) return "text-red-600 dark:text-red-400";
            if (this.finalScore < 75) return "text-amber-600 dark:text-amber-400";
            return "text-emerald-600 dark:text-emerald-400";
        },
        rawScore() {
            if (this.trace) return this.trace.score_raw;
            const scores = this.score?.scores;
            if (!scores || scores.length === 0) return null;
            return scores[scores.length - 1].score;
        },
        ceiling() {
            return this.trace ? this.trace.ceiling : null;
        },
        /**
         * Modelo unificado de linha: vem do trace quando existe; senão é
         * derivado do json de scores do run (applied, ou delta do running total).
         */
        rows() {
            if (this.trace) {
                return this.trace.spans.map((span) => ({
                    rule: span.rule_name,
                    label: this.formatRuleName(span.rule_name),
                    delta: span.weight,
                    valueLabel: this.formatRowValue(span.value),
                    guard: span.guard,
                    ceiling: span.ceiling,
                    flags: this.activeFlags(span.flags),
                }));
            }

            const scores = this.score?.scores || [];
            let previous = 100;

            return scores.map((item) => {
                const delta = item.applied ?? item.score - previous;
                previous = item.score;

                return {
                    rule: item.rule,
                    label: item.description || this.formatRuleName(item.rule),
                    delta,
                    valueLabel: this.formatRowValue(item.value),
                    guard: null,
                    ceiling: item.ceiling ?? null,
                    flags: [],
                };
            });
        },
        orderedRows() {
            return [...this.rows].sort((a, b) => a.delta - b.delta);
        },
        absorbedPenalty() {
            if (this.rawScore === null || this.rawScore <= 100) return 0;
            const negative = this.rows
                .filter((row) => row.delta < 0)
                .reduce((sum, row) => sum + row.delta, 0);
            return Math.min(Math.abs(negative), this.rawScore - 100);
        },
    },
    watch: {
        scoreId() {
            this.fetch();
        },
    },
    mounted() {
        this.fetch();
    },
    methods: {
        gaugePct(value) {
            return Math.max(0, Math.min(100, ((value - 15) / 85) * 100));
        },
        fetch() {
            this.loading = true;
            this.modalOpen = false;

            const url = this.scoreId
                ? `/api/user/${this.user.id}/score/${this.scoreId}/trace`
                : `/api/user/${this.user.id}/score/trace/latest`;

            axios
                .get(url)
                .then(({ data }) => {
                    this.trace = data.trace;
                })
                .catch(() => {
                    this.trace = null;
                })
                .finally(() => {
                    this.loading = false;
                });
        },
        openModalAt(ruleName) {
            if (!this.trace) return;
            this.modalRule = ruleName;
            this.modalOpen = true;
        },
        activeFlags(flags) {
            if (!flags) return [];
            return Object.entries(flags)
                .filter(([, value]) => value === true)
                .map(([key]) => key.replace(/_/g, " "));
        },
        formatRowValue(value) {
            if (value === null || value === undefined) return null;
            if (typeof value === "boolean") return value ? "sim" : "não";
            if (typeof value === "number") return Number.isInteger(value) ? String(value) : value.toFixed(2);
            return String(value);
        },
        formatRuleName(ruleName) {
            return scoreRuleLabels[ruleName] || (ruleName || "").replace(/_/g, " ");
        },
    },
};
</script>
